import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from '../../../utility/axios';
import { Input, Button, message, Spin, Badge, Avatar, Tag, Checkbox, Tooltip, Dropdown, Space, Modal } from 'antd';
import { 
    SearchOutlined, UserOutlined, MailOutlined, 
    PhoneOutlined, HeartFilled, HeartOutlined, CalendarOutlined, EditOutlined, PlusOutlined,
    DeleteOutlined, RollbackOutlined, SaveOutlined, MinusOutlined, DownOutlined, UpOutlined
} from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import ContactGroupModal from './ContactGroupModal';
import '../../../styles/settings/settings-common.css';
import '../../../styles/settings/contacts.css';

const Contacts = ({ embedded, unifiedPage }) => {
    const { t } = useTranslation();
    
    // Get the initial tab from localStorage or default to 'recents'
    const getInitialTab = () => {
        const savedTab = localStorage.getItem('contactsActiveTab');
        return savedTab || 'recents';
    };

    const getInitialSortConfig = () => {
        try {
            const saved = localStorage.getItem('contactsSortConfig');
            if (saved) {
                const parsed = JSON.parse(saved);
                const validKeys = ['email', 'name', 'phone', 'created_at'];
                if (validKeys.includes(parsed.key) && ['ascend', 'descend'].includes(parsed.direction)) {
                    return parsed;
                }
            }
        } catch (_) {}
        return { key: null, direction: 'ascend' };
    };

    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [searchText, setSearchText] = useState('');
    const [selectedContact, setSelectedContact] = useState(null);
    const [selectedContacts, setSelectedContacts] = useState([]);
    const [activeTab, setActiveTab] = useState(getInitialTab);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [modalType, setModalType] = useState('contact');
    const [editingContact, setEditingContact] = useState(null);
    const [editFormData, setEditFormData] = useState({
        firstname: '',
        lastname: '',
        email: '',
        phone: ''
    });
    const [groupMembers, setGroupMembers] = useState([]);
    const [newMemberEmail, setNewMemberEmail] = useState('');
    const [editingGroupName, setEditingGroupName] = useState(false);
    const [contactEdits, setContactEdits] = useState({});
    const [groupEdits, setGroupEdits] = useState({});
    const [groupAddModalKey, setGroupAddModalKey] = useState(null);
    const [groupAddModalSearchText, setGroupAddModalSearchText] = useState('');
    const [groupAddModalDropdownOpen, setGroupAddModalDropdownOpen] = useState(false);
    const [groupExpanded, setGroupExpanded] = useState(new Set());
    const [sortConfig, setSortConfig] = useState(getInitialSortConfig);

    // Count of contacts and groups by type
    const contactCounts = useMemo(() => {
        return {
            recents: contacts.filter(c => c._type === 'contact' && c.contact_type === 'recents').length,
            favorite: contacts.filter(c => c.contact_type === 'favorite').length,
            deleted: contacts.filter(c => c.contact_type === 'deleted').length,
            groups: contacts.filter(c => c._type === 'group' && c.contact_type === 'groups').length
        };
    }, [contacts]);

    // Available tabs configuration
    const availableTabs = [
        { id: 'recents', label: t('contacts.recents'), count: contactCounts.recents, badgeColor: '#5E5BFF' },
        { id: 'favorite', label: t('contacts.favorite'), count: contactCounts.favorite, badgeColor: '#faad14' },
        { id: 'groups', label: t('contacts.groups'), count: contactCounts.groups, badgeColor: '#52c41a' },
        { id: 'deleted', label: t('contacts.deleted'), count: contactCounts.deleted, badgeColor: '#ff4d4f' },
    ];

    // Handle tab change
    const handleTabChange = (tabId) => {
        setActiveTab(tabId);
        setSelectedContact(null);
        setSearchText('');
        setSelectedContacts([]);
        localStorage.setItem('contactsActiveTab', tabId);
    };

    // Handle contact selection
    const handleContactSelect = (record, event) => {
        // If Ctrl or Cmd is held, toggle checkbox instead
        if (event && (event.ctrlKey || event.metaKey)) {
            const isSelected = selectedContacts.includes(record.key);
            
            if (isSelected) {
                setSelectedContacts(prev => prev.filter(k => k !== record.key));
            } else {
                setSelectedContacts(prev => [...prev, record.key]);
            }
        } else {
            setSelectedContact(record);
            setSelectedContacts([record.key]);
            
            if (record._type === 'group') {
                const members = (record.members || []).map(m => ({
                    id: m.id,
                    email: m.email || m,
                    firstname: m.firstname || '',
                    lastname: m.lastname || '',
                    phone: m.phone || ''
                }));
                if (members.length === 0 && record.email) {
                    record.email.split(';').forEach(e => {
                        const trimmed = e.trim();
                        if (trimmed) members.push({ email: trimmed, firstname: '', lastname: '', phone: '' });
                    });
                }
                setEditFormData({
                    firstname: record.group_name || record.firstname || '',
                    lastname: '',
                    email: record.email || '',
                    phone: ''
                });
                setGroupMembers(members);
                setEditingGroupName(false);
            } else {
                // Initialize edit form data when selecting a contact
                setEditFormData({
                    firstname: record.firstname || '',
                    lastname: record.lastname || '',
                    email: record.email || '',
                    phone: record.phone || ''
                });
                setGroupMembers([]);
            }
            
            // Reset new member email input
            setNewMemberEmail('');
        }
    };

    // Handle checkbox selection
    const handleCheckboxChange = (recordKey, checked, e) => {
        e.stopPropagation();
        
        if (checked) {
            setSelectedContacts(prev => [...prev, recordKey]);
        } else {
            setSelectedContacts(prev => prev.filter(k => k !== recordKey));
        }
    };

    useEffect(() => {
        fetchContacts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (initialLoading || embedded) return;
        const tabContacts = filterData(contacts.filter(contact => contact.contact_type === activeTab));
        const selectedInTab = selectedContact && tabContacts.some(c => c.key === selectedContact.key);
        if (tabContacts.length > 0 && (!selectedContact || !selectedInTab)) {
            const firstContact = tabContacts[0];
            setSelectedContact(firstContact);
            setSelectedContacts([firstContact.key]);

            if (firstContact._type === 'group') {
                const members = (firstContact.members || []).map(m => ({
                    id: m.id,
                    email: m.email || m,
                    firstname: m.firstname || '',
                    lastname: m.lastname || '',
                    phone: m.phone || ''
                }));
                if (members.length === 0 && firstContact.email) {
                    firstContact.email.split(';').forEach(e => {
                        const trimmed = e.trim();
                        if (trimmed) members.push({ email: trimmed, firstname: '', lastname: '', phone: '' });
                    });
                }
                setEditFormData({
                    firstname: firstContact.group_name || firstContact.firstname || '',
                    lastname: '',
                    email: firstContact.email || '',
                    phone: ''
                });
                setGroupMembers(members);
            } else {
                setEditFormData({
                    firstname: firstContact.firstname || '',
                    lastname: firstContact.lastname || '',
                    email: firstContact.email || '',
                    phone: firstContact.phone || ''
                });
                setGroupMembers([]);
            }
            setNewMemberEmail('');
        }
    }, [initialLoading, contacts, activeTab, selectedContact, searchText]);

    const updateContactsApi = (payload) => axios.post('/api/profile/contacts/update/', payload);

    const fetchContacts = async () => {
        setLoading(true);
        try {
            const response = await axios.get('/api/profile/contacts/');
            const contactsList = (response.data.contacts || []).map(c => ({
                ...c,
                key: `contact-${c.id}`,
                _type: 'contact'
            }));
            const groupsList = (response.data.groups || []).map(g => ({
                ...g,
                key: `group-${g.id}`,
                _type: 'group',
                firstname: g.group_name || '',
                email: g.email || ''
            }));
            const allContacts = [...contactsList, ...groupsList];
            setContacts(allContacts);
            return allContacts;
        } catch (error) {
            console.error('Error fetching contacts:', error);
            message.error(t('contacts.failedToLoad'));
            return [];
        } finally {
            setLoading(false);
            setInitialLoading(false);
        }
    };

    // Handle field change
    const handleFieldChange = (field, value) => {
        setEditFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Handle remove member from group
    const handleRemoveMember = (emailToRemove, e) => {
        if (e) e.stopPropagation();
        setGroupMembers(prev => prev.filter(m => m.email !== emailToRemove));
    };

    const handleMemberFieldChange = (email, field, value) => {
        setGroupMembers(prev => prev.map(m => m.email === email ? { ...m, [field]: value } : m));
    };

    // Validate email format
    const isValidEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    // Handle add member to group
    const handleAddMember = () => {
        const trimmedEmail = newMemberEmail.trim();
        
        if (!trimmedEmail) {
            message.warning(t('contacts.emailRequired'));
            return;
        }
        
        if (!isValidEmail(trimmedEmail)) {
            message.error(t('contacts.invalidEmails', { emails: trimmedEmail }));
            return;
        }
        
        if (groupMembers.some(m => m.email.toLowerCase() === trimmedEmail.toLowerCase())) {
            message.warning(t('contacts.memberAlreadyInGroup'));
            return;
        }
        
        const contact = contacts.find(c => c._type !== 'group' && c.contact_type !== 'deleted' && c.email?.toLowerCase() === trimmedEmail.toLowerCase());
        setGroupMembers(prev => [...prev, contact
            ? { email: contact.email, firstname: contact.firstname || '', lastname: contact.lastname || '', phone: contact.phone || '' }
            : { email: trimmedEmail, firstname: '', lastname: '', phone: '' }
        ]);
        setNewMemberEmail('');
        message.success(t('contacts.memberAddedToGroup'));
    };

    // Handle add single contact to favorites
    const handleAddToFavorites = async () => {
        if (!selectedContact) return;
        
        if (selectedContact.contact_type === 'favorite') {
            message.info(t('contacts.alreadyInFavorites'));
            return;
        }
        
        setLoading(true);
        try {
            await updateContactsApi({ ids: [selectedContact.id], action: 'add_favorite' });
            
            setContacts(prevContacts => 
                prevContacts.map(contact => 
                    contact.key === selectedContact.key 
                        ? { ...contact, contact_type: 'favorite' }
                        : contact
                )
            );
            
            setSelectedContact(prev => ({ ...prev, contact_type: 'favorite' }));
            setActiveTab('favorite');
            localStorage.setItem('contactsActiveTab', 'favorite');
            message.success(t('contacts.addedToFavorites'));
        } catch (error) {
            console.error('Error adding to favorites:', error);
            message.error(t('contacts.failedToAddFavorite'));
        } finally {
            setLoading(false);
        }
    };

    // Toggle favorite for a record (contact or group) from the card
    const handleToggleFavorite = async (record, e) => {
        if (e) e.stopPropagation();
        const isFavorite = record.contact_type === 'favorite';
        setLoading(true);
        try {
            await updateContactsApi({
                ids: [record.id],
                action: isFavorite ? 'remove_favorite' : 'add_favorite'
            });
            const targetType = record._type === 'group' ? 'groups' : 'recents';
            setContacts(prev => prev.map(c => c.key === record.key
                ? { ...c, contact_type: isFavorite ? targetType : 'favorite' }
                : c));
            if (selectedContact?.key === record.key) {
                setSelectedContact(prev => prev ? { ...prev, contact_type: isFavorite ? targetType : 'favorite' } : null);
            }
            if (!isFavorite) {
                setActiveTab('favorite');
                localStorage.setItem('contactsActiveTab', 'favorite');
            }
            message.success(isFavorite ? t('contacts.removedFromFavorites') : t('contacts.addedToFavorites'));
        } catch (error) {
            console.error('Error toggling favorite:', error);
            message.error(isFavorite ? t('contacts.failedToRemoveFavorite') : t('contacts.failedToAddFavorite'));
        } finally {
            setLoading(false);
        }
    };

    // Handle remove single contact from favorites
    const handleRemoveFromFavorites = async () => {
        if (!selectedContact) return;
        
        setLoading(true);
        try {
            await updateContactsApi({ ids: [selectedContact.id], action: 'remove_favorite' });

            setContacts(prevContacts => 
                prevContacts.map(contact => 
                    contact.key === selectedContact.key 
                        ? { ...contact, contact_type: selectedContact._type === 'group' ? 'groups' : 'recents' }
                        : contact
                )
            );
            
            setSelectedContact(null);
            message.success(t('contacts.removedFromFavorites'));
        } catch (error) {
            console.error('Error removing from favorites:', error);
            message.error(t('contacts.failedToRemoveFavorite'));
        } finally {
            setLoading(false);
        }
    };

    // Handle delete single contact (soft delete)
    const handleDeleteContact = async () => {
        if (!selectedContact) return;
        
        setLoading(true);
        try {
            await updateContactsApi({ ids: [selectedContact.id], action: 'delete' });
            
            setContacts(prevContacts => 
                prevContacts.map(contact => 
                    contact.key === selectedContact.key 
                        ? { ...contact, contact_type: 'deleted' }
                        : contact
                )
            );
            
            setSelectedContact(null);
            message.success(t('contacts.contactMoved'));
        } catch (error) {
            console.error('Error deleting contact:', error);
            message.error(t('contacts.failedToDelete'));
        } finally {
            setLoading(false);
        }
    };

    // Handle restore single contact
    const handleRestoreContact = async () => {
        if (!selectedContact) return;
        
        setLoading(true);
        try {
            await updateContactsApi({ ids: [selectedContact.id], action: 'restore' });

            setContacts(prevContacts => 
                prevContacts.map(contact => 
                    contact.key === selectedContact.key 
                        ? { ...contact, contact_type: selectedContact._type === 'group' ? 'groups' : 'recents' }
                        : contact
                )
            );
            
            setSelectedContact(null);
            message.success(t('contacts.contactRestored'));
        } catch (error) {
            console.error('Error restoring contact:', error);
            message.error(t('contacts.failedToRestore'));
        } finally {
            setLoading(false);
        }
    };

    // Handle permanent delete single contact
    const handlePermanentDeleteContact = async () => {
        if (!selectedContact) return;
        
        setLoading(true);
        try {
            await updateContactsApi({ ids: [selectedContact.id], action: 'permanent_delete' });
            
            setContacts(prevContacts => 
                prevContacts.filter(contact => contact.key !== selectedContact.key)
            );
            
            setSelectedContact(null);
            message.success(t('contacts.contactDeleted'));
        } catch (error) {
            console.error('Error permanently deleting contact:', error);
            message.error(t('contacts.failedToPermanentDelete'));
        } finally {
            setLoading(false);
        }
    };

    const handleSaveChanges = async () => {
        if (selectedContact?._type === 'group') {
            if (!editFormData.firstname) {
                message.error(t('contacts.groupNameRequired'));
                return;
            }
            if (groupMembers.length === 0) {
                message.error(t('contacts.groupMembersRequired'));
                return;
            }
            if (groupMembers.length < 2) {
                message.error(t('contacts.groupMinimumMembers'));
                return;
            }
            setLoading(true);
            try {
                const members = groupMembers.map(m => ({
                    email: m.email,
                    firstname: m.firstname || '',
                    lastname: m.lastname || '',
                    phone: m.phone || ''
                }));
                const { data } = await updateContactsApi({
                    group_id: selectedContact.id,
                    action: 'update',
                    group_name: editFormData.firstname,
                    members
                });
                const group = data?.contact_group || {};
                const updatedMembers = (group.members || []).map(m => ({
                    id: m.id,
                    email: m.email,
                    firstname: m.firstname || '',
                    lastname: m.lastname || '',
                    phone: m.phone || ''
                }));
                const groupEmail = updatedMembers.map(m => m.email).join(';');
                const updatedData = {
                    group_name: editFormData.firstname,
                    firstname: editFormData.firstname,
                    email: groupEmail,
                    members: updatedMembers
                };
                setContacts(prev => prev.map(c => c.key === selectedContact.key ? { ...c, ...updatedData } : c));
                setSelectedContact(prev => ({ ...prev, ...updatedData }));
                setGroupMembers(updatedMembers);
                message.success(t('contacts.contactUpdated'));
            } catch (error) {
                console.error('Error updating group:', error);
                message.error(error.response?.data?.error || t('contacts.failedToUpdate'));
            } finally {
                setLoading(false);
            }
        } else {
            // Regular contact update
            setLoading(true);
            try {
                await updateContactsApi({
                    ids: [selectedContact.id],
                    action: 'update',
                    firstname: editFormData.firstname,
                    lastname: editFormData.lastname,
                    email: selectedContact.email,
                    phone: editFormData.phone
                });

                // Update local state (email remains unchanged)
                const updatedData = {
                    firstname: editFormData.firstname,
                    lastname: editFormData.lastname,
                    phone: editFormData.phone
                };
                
                setContacts(prevContacts => 
                    prevContacts.map(c => 
                        c.key === selectedContact.key 
                            ? { ...c, ...updatedData }
                            : c
                    )
                );

                // Update selected contact
                setSelectedContact(prev => ({ ...prev, ...updatedData }));

                message.success(t('contacts.contactUpdated'));
            } catch (error) {
                console.error('Error updating contact:', error);
                message.error(error.response?.data?.error || t('contacts.failedToUpdate'));
            } finally {
                setLoading(false);
            }
        }
    };

    // Handle add new (opens modal with contact form, toggle to switch to group)
    const handleAddNew = () => {
        setEditingContact(null);
        setModalType('contact');
        setIsModalVisible(true);
    };

    const handleEditContact = (record) => {
        setEditingContact(record);
        setModalType(record._type === 'group' ? 'group' : 'contact');
        setIsModalVisible(true);
    };

    const getContactEditValue = (record, field) => {
        const key = record.key;
        if (contactEdits[key] && field in contactEdits[key]) return contactEdits[key][field];
        return (record[field] || '').toString();
    };

    const handleContactFieldChange = (recordKey, field, value) => {
        setContactEdits(prev => ({
            ...prev,
            [recordKey]: {
                ...(prev[recordKey] || {}),
                [field]: value
            }
        }));
    };

    const filterContactsForGroup = (searchText) => {
        const list = contacts.filter(c => c._type !== 'group' && c.contact_type !== 'deleted');
        if (!searchText?.trim()) return list;
        const lower = searchText.toLowerCase();
        return list.filter(c => {
            const email = (c.email || '').toLowerCase();
            const name = `${c.firstname || ''} ${c.lastname || ''}`.trim().toLowerCase();
            const phone = (c.phone || '').toLowerCase();
            return email.includes(lower) || name.includes(lower) || phone.includes(lower);
        });
    };

    const getGroupMembers = (record) => {
        const edits = groupEdits[record.key];
        if (edits?.members) return edits.members;
        const members = (record.members || []).map(m => ({
            email: m.email || m,
            firstname: m.firstname || '',
            lastname: m.lastname || '',
            phone: m.phone || ''
        }));
        if (members.length === 0 && record.email) {
            record.email.split(';').forEach(e => {
                const trimmed = e.trim();
                if (trimmed) members.push({ email: trimmed, firstname: '', lastname: '', phone: '' });
            });
        }
        return members;
    };

    const getGroupName = (record) => {
        const edits = groupEdits[record.key];
        if (edits?.name !== undefined) return edits.name;
        return record.group_name || record.firstname || '';
    };

    const handleGroupNameChange = (key, value) => {
        setGroupEdits(prev => ({ ...prev, [key]: { ...(prev[key] || {}), name: value } }));
    };

    const handleAddGroupMember = (groupKey, member) => {
        const record = contacts.find(c => c.key === groupKey);
        if (!record) return;
        const members = getGroupMembers(record);
        if (members.some(m => m.email?.toLowerCase() === member.email?.toLowerCase())) return;
        setGroupEdits(prev => ({
            ...prev,
            [groupKey]: {
                ...(prev[groupKey] || {}),
                members: [...members, member]
            }
        }));
    };

    const handleRemoveGroupMember = (groupKey, index) => {
        const record = contacts.find(c => c.key === groupKey);
        if (!record) return;
        const members = getGroupMembers(record).filter((_, i) => i !== index);
        setGroupEdits(prev => ({
            ...prev,
            [groupKey]: { ...(prev[groupKey] || {}), members }
        }));
    };

    const handleRemoveGroupMemberByEmail = (groupKey, email) => {
        const record = contacts.find(c => c.key === groupKey);
        if (!record) return;
        const members = getGroupMembers(record).filter(m => m.email?.toLowerCase() !== email?.toLowerCase());
        setGroupEdits(prev => ({
            ...prev,
            [groupKey]: { ...(prev[groupKey] || {}), members }
        }));
    };

    const handleGroupMemberFieldChange = (groupKey, memberIndex, field, value) => {
        const record = contacts.find(c => c.key === groupKey);
        if (!record) return;
        const members = getGroupMembers(record);
        const updated = [...members];
        updated[memberIndex] = { ...updated[memberIndex], [field]: value };
        setGroupEdits(prev => ({ ...prev, [groupKey]: { ...(prev[groupKey] || {}), members: updated } }));
    };

    const handleSaveAllContacts = async () => {
        const keysToSave = Object.keys(contactEdits).filter(k => {
            const record = contacts.find(c => c.key === k);
            return record && record._type !== 'group';
        });
        if (keysToSave.length === 0) return;
        setLoading(true);
        for (const key of keysToSave) {
            const record = contacts.find(c => c.key === key);
            if (!record) continue;
            const edits = contactEdits[key] || {};
            const firstname = (edits.firstname ?? record.firstname ?? '').toString();
            const lastname = (edits.lastname ?? record.lastname ?? '').toString();
            const phone = (edits.phone ?? record.phone ?? '').toString();
            try {
                await updateContactsApi({
                    ids: [record.id],
                    action: 'update',
                    firstname,
                    lastname,
                    email: record.email,
                    phone
                });
                setContacts(prev => prev.map(c => c.key === key ? { ...c, firstname, lastname, phone } : c));
                setContactEdits(prev => {
                    const next = { ...prev };
                    delete next[key];
                    return next;
                });
            } catch (error) {
                message.error(error.response?.data?.error || t('contacts.failedToUpdate'));
                break;
            }
        }
        if (keysToSave.length > 0) message.success(t('contacts.contactUpdated'));
        setLoading(false);
    };

    const handleSaveAllGroups = async () => {
        const keysToSave = Object.keys(groupEdits).filter(k => {
            const record = contacts.find(c => c.key === k);
            return record && record._type === 'group';
        });
        if (keysToSave.length === 0) return;
        setLoading(true);
        for (const key of keysToSave) {
            const record = contacts.find(c => c.key === key);
            if (!record) continue;
            const edits = groupEdits[key] || {};
            const name = (edits.name ?? record.group_name ?? record.firstname ?? '').toString();
            const members = edits.members ?? getGroupMembers(record);
            if (!name) {
                message.error(t('contacts.groupNameRequired'));
                setLoading(false);
                return;
            }
            if (members.length < 2) {
                message.error(t('contacts.groupMinimumMembers'));
                setLoading(false);
                return;
            }
            try {
                const { data } = await updateContactsApi({
                    group_id: record.id,
                    action: 'update',
                    group_name: name,
                    members: members.map(m => ({
                        email: m.email,
                        firstname: m.firstname || '',
                        lastname: m.lastname || '',
                        phone: m.phone || ''
                    }))
                });
                const group = data?.contact_group || {};
                const updatedMembers = (group.members || []).map(m => ({
                    id: m.id,
                    email: m.email || m,
                    firstname: m.firstname || '',
                    lastname: m.lastname || '',
                    phone: m.phone || ''
                }));
                const groupEmail = updatedMembers.map(m => m.email).join(';');
                setContacts(prev => prev.map(c => c.key === key ? {
                    ...c,
                    group_name: name,
                    firstname: name,
                    email: groupEmail,
                    members: updatedMembers
                } : c));
                setGroupEdits(prev => {
                    const next = { ...prev };
                    delete next[key];
                    return next;
                });
                message.success(t('contacts.contactUpdated'));
            } catch (error) {
                message.error(error.response?.data?.error || t('contacts.failedToUpdate'));
                break;
            }
        }
        setLoading(false);
    };

    const handleSaveAll = async () => {
        const hasContactEdits = Object.keys(contactEdits).some(k => {
            const r = contacts.find(c => c.key === k);
            return r && r._type !== 'group';
        });
        const hasGroupEdits = Object.keys(groupEdits).length > 0;
        if (hasContactEdits) await handleSaveAllContacts();
        if (hasGroupEdits) await handleSaveAllGroups();
    };

    const hasEdits = Object.keys(contactEdits).some(k => {
        const r = contacts.find(c => c.key === k);
        return r && r._type !== 'group';
    }) || Object.keys(groupEdits).length > 0;

    const handleAdded = async (createdItem, type) => {
        const allContacts = await fetchContacts();
        setEditingContact(null);
        if (createdItem && type) {
            const targetTab = type === 'contact' ? 'recents' : 'groups';
            setActiveTab(targetTab);
            localStorage.setItem('contactsActiveTab', targetTab);
            const key = type === 'contact' ? `contact-${createdItem.id}` : `group-${createdItem.id}`;
            const record = allContacts.find(c => c.key === key);
            if (record) {
                setSelectedContact(record);
                setSelectedContacts([record.key]);
                setSearchText('');
                if (record._type === 'group') {
                    const members = (record.members || []).map(m => ({
                        id: m.id,
                        email: m.email || m,
                        firstname: m.firstname || '',
                        lastname: m.lastname || '',
                        phone: m.phone || ''
                    }));
                    if (members.length === 0 && record.email) {
                        record.email.split(';').forEach(e => {
                            const trimmed = e.trim();
                            if (trimmed) members.push({ email: trimmed, firstname: '', lastname: '', phone: '' });
                        });
                    }
                    setEditFormData({
                        firstname: record.group_name || record.firstname || '',
                        lastname: '',
                        email: record.email || '',
                        phone: ''
                    });
                    setGroupMembers(members);
                } else {
                    setEditFormData({
                        firstname: record.firstname || '',
                        lastname: record.lastname || '',
                        email: record.email || '',
                        phone: record.phone || ''
                    });
                    setGroupMembers([]);
                }
                setNewMemberEmail('');
            }
        }
    };

    // Handle batch delete
    const handleBatchDelete = async () => {
        if (selectedContacts.length === 0) {
            message.warning(t('contacts.selectAtLeastOne'));
            return;
        }

        setLoading(true);
        try {
            const selectedItems = selectedContacts.map(k => contacts.find(c => c.key === k)).filter(Boolean);
            await updateContactsApi({ ids: selectedItems.map(i => i.id), action: 'delete' });
            
            setContacts(prevContacts => 
                prevContacts.map(contact => 
                    selectedContacts.includes(contact.key) 
                        ? { ...contact, contact_type: 'deleted' }
                        : contact
                )
            );
            
            message.success(t('contacts.batchContactsMoved', { count: selectedContacts.length }));
            setSelectedContacts([]);
        } catch (error) {
            console.error('Error deleting contacts:', error);
            message.error(t('contacts.batchDeleteFailed'));
        } finally {
            setLoading(false);
        }
    };

    // Handle batch restore
    const handleBatchRestore = async () => {
        if (selectedContacts.length === 0) {
            message.warning(t('contacts.selectAtLeastOne'));
            return;
        }

        setLoading(true);
        try {
            const selectedItems = selectedContacts.map(k => contacts.find(c => c.key === k)).filter(Boolean);
            await updateContactsApi({ ids: selectedItems.map(i => i.id), action: 'restore' });
            
            setContacts(prevContacts => 
                prevContacts.map(contact => {
                    if (selectedContacts.includes(contact.key)) {
                        return { ...contact, contact_type: contact._type === 'group' ? 'groups' : 'recents' };
                    }
                    return contact;
                })
            );

            message.success(t('contacts.batchContactsRestored', { count: selectedContacts.length }));
            setSelectedContacts([]);
        } catch (error) {
            console.error('Error restoring contacts:', error);
            message.error(t('contacts.batchRestoreFailed'));
        } finally {
            setLoading(false);
        }
    };

    // Handle batch add to favorites
    const handleBatchAddToFavorites = async () => {
        if (selectedContacts.length === 0) {
            message.warning(t('contacts.selectAtLeastOne'));
            return;
        }

        setLoading(true);
        try {
            const contactsToUpdate = contacts
                .filter(contact => selectedContacts.includes(contact.key) && contact.contact_type !== 'favorite');
            
            if (contactsToUpdate.length === 0) {
                message.info(t('contacts.favoritesAlreadyInFavorites'));
                setLoading(false);
                return;
            }

            await updateContactsApi({ ids: contactsToUpdate.map(c => c.id), action: 'add_favorite' });
            
            setContacts(prevContacts => 
                prevContacts.map(contact => 
                    contactsToUpdate.some(c => c.key === contact.key)
                        ? { ...contact, contact_type: 'favorite' }
                        : contact
                )
            );
            
            setActiveTab('favorite');
            localStorage.setItem('contactsActiveTab', 'favorite');
            message.success(t('contacts.batchContactsAddedToFavorites', { count: contactsToUpdate.length }));
            setSelectedContacts([]);
        } catch (error) {
            console.error('Error adding contacts to favorites:', error);
            message.error(t('contacts.batchFavoriteFailed'));
        } finally {
            setLoading(false);
        }
    };

    // Handle batch remove from favorites
    const handleBatchRemoveFromFavorites = async () => {
        if (selectedContacts.length === 0) {
            message.warning(t('contacts.selectAtLeastOne'));
            return;
        }

        setLoading(true);
        try {
            const selectedItems = selectedContacts.map(k => contacts.find(c => c.key === k)).filter(Boolean);
            await updateContactsApi({ ids: selectedItems.map(i => i.id), action: 'remove_favorite' });
            
            setContacts(prevContacts => 
                prevContacts.map(contact => {
                    if (selectedContacts.includes(contact.key)) {
                        return { ...contact, contact_type: contact._type === 'group' ? 'groups' : 'recents' };
                    }
                    return contact;
                })
            );

            message.success(t('contacts.batchContactsRemovedFromFavorites', { count: selectedContacts.length }));
            setSelectedContacts([]);
        } catch (error) {
            console.error('Error removing contacts from favorites:', error);
            message.error(t('contacts.batchUnfavoriteFailed'));
        } finally {
            setLoading(false);
        }
    };

    // Handle batch permanent delete
    const handleBatchPermanentDelete = async () => {
        if (selectedContacts.length === 0) {
            message.warning(t('contacts.selectAtLeastOne'));
            return;
        }

        setLoading(true);
        try {
            const selectedItems = selectedContacts.map(k => contacts.find(c => c.key === k)).filter(Boolean);
            await updateContactsApi({ ids: selectedItems.map(i => i.id), action: 'permanent_delete' });
            
            setContacts(prevContacts => 
                prevContacts.filter(contact => !selectedContacts.includes(contact.key))
            );
            
            message.success(t('contacts.batchContactsDeleted', { count: selectedContacts.length }));
            setSelectedContacts([]);
        } catch (error) {
            console.error('Error permanently deleting contacts:', error);
            message.error(t('contacts.batchPermanentDeleteFailed'));
        } finally {
            setLoading(false);
        }
    };

    // Filter data based on search text
    const filterData = (data) => {
        if (!searchText) return data;
        
        return data.filter(record => {
            const name = `${record.group_name || ''} ${record.firstname || ''} ${record.lastname || ''}`.trim().toLowerCase();
            const email = record.email.toLowerCase();
            const phone = record.phone ? record.phone.toLowerCase() : '';
            const searchLower = searchText.toLowerCase();
            
            return name.includes(searchLower) || 
                   email.includes(searchLower) ||
                   phone.includes(searchLower);
        });
    };

    // Get current data based on active tab
    const getCurrentData = () => {
        const tabContacts = contacts.filter(contact => contact.contact_type === activeTab);
        return filterData(tabContacts);
    };

    const sortContactsData = (data, config) => {
        if (!config.key) return data;
        const dir = config.direction === 'ascend' ? 1 : -1;
        return [...data].sort((a, b) => {
            let va, vb;
            switch (config.key) {
                case 'email':
                    va = (a.email || '').toLowerCase();
                    vb = (b.email || '').toLowerCase();
                    return dir * va.localeCompare(vb);
                case 'name':
                    va = `${a.firstname || ''} ${a.lastname || ''}`.trim().toLowerCase();
                    vb = `${b.firstname || ''} ${b.lastname || ''}`.trim().toLowerCase();
                    return dir * va.localeCompare(vb);
                case 'phone':
                    va = (a.phone || '').toLowerCase();
                    vb = (b.phone || '').toLowerCase();
                    return dir * va.localeCompare(vb);
                case 'created_at':
                    va = a.created_at ? new Date(a.created_at).getTime() : 0;
                    vb = b.created_at ? new Date(b.created_at).getTime() : 0;
                    return dir * (va - vb);
                default:
                    return 0;
            }
        });
    };

    // Get initials from name
    const getInitials = (firstname, lastname) => {
        const first = firstname ? firstname.charAt(0) : '';
        const last = lastname ? lastname.charAt(0) : '';
        return `${first}${last}`.toUpperCase() || 'C';
    };

    // Get avatar color based on name
    const getAvatarColor = (name) => {
        const colors = ['#5E5BFF', '#FF5E5B', '#FFCE5E', '#5EFFCE', '#CE5EFF', '#5E9EFF'];
        const charCode = name ? name.charCodeAt(0) : 0;
        return colors[charCode % colors.length];
    };

    const currentData = getCurrentData();
    const sortedData = useMemo(() => sortContactsData(currentData, sortConfig), [currentData, sortConfig]);

    if (initialLoading) {
        return (
            <div className="contacts-container" data-settings-scroll>
                <div className="contacts-loading-state">
                    <Spin size="large" />
                </div>
            </div>
        );
    }

    const hasSelection = selectedContacts.length > 0;

    const sortMenuItems = [
        { key: 'email', icon: MailOutlined, labelKey: 'sortByEmail' },
        { key: 'name', icon: UserOutlined, labelKey: 'sortByName' },
        { key: 'phone', icon: PhoneOutlined, labelKey: 'sortByPhone' },
        { key: 'created_at', icon: CalendarOutlined, labelKey: 'sortByAddedAt' },
    ];
    const sortDropdown = (
        <div className="contacts-sort-dropdown">
            <Dropdown
                menu={{
                    items: sortMenuItems.map(({ key, icon: Icon, labelKey }) => ({
                        key,
                        label: (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <span><Icon style={{ marginRight: '8px' }} />{t(`contacts.${labelKey}`)}</span>
                                {sortConfig.key === key && (sortConfig.direction === 'ascend' ? '↑' : '↓')}
                            </div>
                        ),
                        onClick: () => {
                        const next = { key, direction: sortConfig.key === key && sortConfig.direction === 'ascend' ? 'descend' : 'ascend' };
                        setSortConfig(next);
                        localStorage.setItem('contactsSortConfig', JSON.stringify(next));
                    }
                    }))
                }}
                trigger={['click']}
            >
                <Button style={{ width: '100%' }}>
                    <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                        <span>
                            {sortConfig.key === null ? t('contacts.sortBy') : (
                                <>{t(`contacts.sortBy${sortConfig.key === 'email' ? 'Email' : sortConfig.key === 'name' ? 'Name' : sortConfig.key === 'phone' ? 'Phone' : 'AddedAt'}`)} {sortConfig.direction === 'ascend' ? '↑' : '↓'}</>
                            )}
                        </span>
                        <DownOutlined />
                    </Space>
                </Button>
            </Dropdown>
        </div>
    );

    return (
        <div className="contacts-container" data-settings-scroll>
            <div className="contacts-panel">
            {/* Tabs + actions row */}
            <div className="contacts-top-bar">
                <div className="contacts-nav-buttons">
                    {availableTabs.map(tab => (
                        <button
                            key={tab.id}
                            className={`contacts-nav-btn ${activeTab === tab.id ? 'active' : ''}`}
                            onClick={() => handleTabChange(tab.id)}
                        >
                            <span>{tab.label}</span>
                            <Badge 
                                count={tab.count} 
                                showZero
                                style={{ 
                                    marginLeft: 4,
                                    backgroundColor: tab.badgeColor,
                                    boxShadow: 'none'
                                }}
                            />
                        </button>
                    ))}
                </div>
                <div className="contacts-nav-actions">
                    {(activeTab === 'recents' || activeTab === 'favorite' || activeTab === 'groups') && (
                                <>
                                    <Tooltip title={activeTab === 'favorite' ? t('contacts.removeFromFavorites') : t('contacts.addToFavorites')} placement="bottom">
                                        {hasSelection ? (
                                            selectedContacts.length >= 2 ? (
                                                <Badge count={selectedContacts.length} size="small" offset={[-2, 2]}>
                                                    <Button 
                                                        type="default"
                                                        icon={<HeartFilled />}
                                                        onClick={activeTab === 'favorite' ? handleBatchRemoveFromFavorites : handleBatchAddToFavorites}
                                                        className="contacts-action-btn contacts-action-btn-favorite contacts-action-btn-icon"
                                                    />
                                                </Badge>
                                            ) : (
                                                <Button 
                                                type="default"
                                                icon={<HeartFilled />}
                                                onClick={activeTab === 'favorite' ? handleBatchRemoveFromFavorites : handleBatchAddToFavorites}
                                                className="contacts-action-btn contacts-action-btn-favorite contacts-action-btn-icon"
                                            />
                                            )
                                        ) : (
                                                <Button 
                                                type="default"
                                                icon={<HeartFilled />}
                                                onClick={activeTab === 'favorite' ? handleBatchRemoveFromFavorites : handleBatchAddToFavorites}
                                                className="contacts-action-btn contacts-action-btn-favorite contacts-action-btn-icon"
                                            />
                                        )}
                                    </Tooltip>
                                    <Tooltip title={t('contacts.delete')} placement="bottom">
                                        {hasSelection ? (
                                            selectedContacts.length >= 2 ? (
                                                <Badge count={selectedContacts.length} size="small" offset={[-2, 2]}>
                                                    <Button 
                                                        danger
                                                        icon={<DeleteOutlined />}
                                                        onClick={handleBatchDelete}
                                                        className="contacts-action-btn contacts-action-btn-destructive contacts-action-btn-icon"
                                                    />
                                                </Badge>
                                            ) : (
                                                <Button 
                                                    danger
                                                    icon={<DeleteOutlined />}
                                                    onClick={handleBatchDelete}
                                                    className="contacts-action-btn contacts-action-btn-destructive contacts-action-btn-icon"
                                                />
                                            )
                                        ) : (
                                            <Button 
                                                danger
                                                icon={<DeleteOutlined />}
                                                onClick={handleBatchDelete}
                                                className="contacts-action-btn contacts-action-btn-destructive contacts-action-btn-icon"
                                            />
                                        )}
                                    </Tooltip>
                                    {activeTab !== 'deleted' && (
                                        <Tooltip title={t('contacts.saveChanges')} placement="bottom">
                                            <Button 
                                                type="primary"
                                                icon={<SaveOutlined />}
                                                onClick={embedded ? handleSaveAll : handleSaveChanges}
                                                loading={loading}
                                                disabled={embedded ? !hasEdits : !selectedContact}
                                                className="contacts-action-btn contacts-action-btn-icon btn-save"
                                                aria-label={t('contacts.saveChanges')}
                                            />
                                        </Tooltip>
                                    )}
                                </>
                            )}
                    {activeTab === 'deleted' && (
                        <>
                            <Tooltip title={t('contacts.addToFavorites')} placement="bottom">
                                {hasSelection ? (
                                    selectedContacts.length >= 2 ? (
                                        <Badge count={selectedContacts.length} size="small" offset={[-2, 2]}>
                                            <Button 
                                                type="default"
                                                icon={<HeartFilled />}
                                                onClick={handleBatchAddToFavorites}
                                                className="contacts-action-btn contacts-action-btn-favorite contacts-action-btn-icon"
                                            />
                                        </Badge>
                                    ) : (
                                        <Button 
                                            type="default"
                                            icon={<HeartFilled />}
                                            onClick={handleBatchAddToFavorites}
                                            className="contacts-action-btn contacts-action-btn-favorite contacts-action-btn-icon"
                                        />
                                    )
                                ) : (
                                    <Button 
                                        type="default"
                                        icon={<HeartFilled />}
                                        onClick={handleBatchAddToFavorites}
                                        className="contacts-action-btn contacts-action-btn-favorite contacts-action-btn-icon"
                                    />
                                )}
                            </Tooltip>
                            <Tooltip title={t('contacts.restore')} placement="bottom">
                                {hasSelection ? (
                                    selectedContacts.length >= 2 ? (
                                        <Badge count={selectedContacts.length} size="small" offset={[-2, 2]}>
                                            <Button 
                                                type="primary"
                                                icon={<RollbackOutlined />}
                                                onClick={handleBatchRestore}
                                                className="contacts-action-btn contacts-action-btn-restore contacts-action-btn-icon"
                                            />
                                        </Badge>
                                    ) : (
                                        <Button 
                                            type="primary"
                                            icon={<RollbackOutlined />}
                                            onClick={handleBatchRestore}
                                            className="contacts-action-btn contacts-action-btn-restore contacts-action-btn-icon"
                                        />
                                    )
                                ) : (
                                    <Button 
                                        type="primary"
                                        icon={<RollbackOutlined />}
                                        onClick={handleBatchRestore}
                                        className="contacts-action-btn contacts-action-btn-restore contacts-action-btn-icon"
                                    />
                                )}
                            </Tooltip>
                            <Tooltip title={t('contacts.permanentDelete')} placement="bottom">
                                {hasSelection ? (
                                    selectedContacts.length >= 2 ? (
                                        <Badge count={selectedContacts.length} size="small" offset={[-2, 2]}>
                                            <Button 
                                                danger
                                                icon={<DeleteOutlined />}
                                                onClick={handleBatchPermanentDelete}
                                                className="contacts-action-btn contacts-action-btn-destructive contacts-action-btn-icon"
                                            />
                                        </Badge>
                                    ) : (
                                        <Button 
                                            danger
                                            icon={<DeleteOutlined />}
                                            onClick={handleBatchPermanentDelete}
                                            className="contacts-action-btn contacts-action-btn-destructive contacts-action-btn-icon"
                                        />
                                    )
                                ) : (
                                    <Button 
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={handleBatchPermanentDelete}
                                        className="contacts-action-btn contacts-action-btn-destructive contacts-action-btn-icon"
                                    />
                                )}
                            </Tooltip>
                        </>
                    )}
                </div>
            </div>

            <div className={embedded ? 'contacts-cards-container' : 'contacts-split-container'}>
                {embedded ? (
                    <>
                        <div className="contacts-search-row contacts-cards-search-row">
                            <Checkbox
                                checked={sortedData.length > 0 && sortedData.every(r => selectedContacts.includes(r.key))}
                                indeterminate={sortedData.some(r => selectedContacts.includes(r.key)) && !sortedData.every(r => selectedContacts.includes(r.key))}
                                onChange={(e) => {
                                    if (e.target.checked) setSelectedContacts(sortedData.map(r => r.key));
                                    else setSelectedContacts([]);
                                }}
                                className="contacts-select-all-checkbox"
                            />
                            <Input
                                placeholder={t('contacts.searchContacts')}
                                prefix={<SearchOutlined />}
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                className="contacts-search-input"
                            />
                            {sortDropdown}
                            <Tooltip title={t('contacts.addNew')} placement="bottom">
                                <Button type="primary" icon={<PlusOutlined />} onClick={handleAddNew} className="contacts-add-btn-inline" aria-label={t('contacts.addNew')} />
                            </Tooltip>
                        </div>
                        <Spin spinning={loading}>
                            <div className={unifiedPage ? 'settings-records-container' : 'contacts-cards-inner'} onWheel={unifiedPage ? (e) => e.stopPropagation() : undefined}>
                                    {sortedData.length === 0 ? (
                                        <div className="contacts-empty contacts-cards-empty">
                                            <UserOutlined style={{ fontSize: '48px', color: '#ccc' }} />
                                            <p>{t('contacts.noContactsFound')}</p>
                                        </div>
                                    ) : (
                                        <div className="contacts-cards-grid">
                                    {sortedData.map((record) => {
                                        const isGroupRecord = record._type === 'group';
                                        if (isGroupRecord) {
                                            const groupMembers = getGroupMembers(record);
                                            const isExpanded = groupExpanded.has(record.key);
                                            return (
                                                <div
                                                    key={record.key}
                                                    className={`contacts-record-card contacts-record-card-group ${selectedContacts.includes(record.key) ? 'selected' : ''} ${isExpanded ? 'expanded' : 'collapsed'}`}
                                                    onClick={(e) => { if (!e.target.closest('.contacts-record-card-checkbox') && !e.target.closest('input') && !e.target.closest('button')) handleContactSelect(record, e); }}
                                                >
                                                    <Checkbox
                                                        checked={selectedContacts.includes(record.key)}
                                                        onChange={(e) => handleCheckboxChange(record.key, e.target.checked, e)}
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="contacts-record-card-checkbox"
                                                    />
                                                    <div className="contacts-record-card-fields contacts-record-card-group-fields">
                                                        <div className={`contacts-record-card-row contacts-record-card-group-name-row ${!isExpanded ? 'contacts-record-card-group-name-row-inline' : ''}`}>
                                                            <Input
                                                                value={getGroupName(record)}
                                                                onChange={(e) => handleGroupNameChange(record.key, e.target.value)}
                                                                placeholder={t('contacts.groupNamePlaceholder')}
                                                                className="contacts-record-card-input contacts-record-card-group-name-input"
                                                                bordered={false}
                                                                onClick={(e) => e.stopPropagation()}
                                                                style={{ width: `${Math.max(14, (getGroupName(record).length || 0) + 2)}ch` }}
                                                            />
                                                            {!isExpanded && groupMembers.length > 0 && (
                                                                <span className="contacts-record-card-group-member-preview">
                                                                    {groupMembers.slice(0, 2).map(m => m.email).join(', ')}
                                                                    {groupMembers.length > 2 && ` +${groupMembers.length - 2}`}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {isExpanded && groupMembers.length > 0 && (
                                                            <div className="contacts-record-card-group-members">
                                                                {groupMembers.map((member, idx) => {
                                                                    const memberContact = contacts.find(c => c._type === 'contact' && c.email?.toLowerCase() === member.email?.toLowerCase());
                                                                    const isMemberFavorite = memberContact?.contact_type === 'favorite';
                                                                    return (
                                                                        <div key={idx} className="contacts-record-card contacts-record-card-editable contacts-record-card-with-favorite contacts-record-card-group-member">
                                                                            <Avatar
                                                                                style={{ backgroundColor: getAvatarColor(member.firstname || member.email) }}
                                                                                size={40}
                                                                            >
                                                                                {getInitials(member.firstname, member.lastname)}
                                                                            </Avatar>
                                                                            <div className="contacts-record-card-fields contacts-record-card-fields--single-line">
                                                                                <Input
                                                                                    value={member.firstname}
                                                                                    onChange={(e) => handleGroupMemberFieldChange(record.key, idx, 'firstname', e.target.value)}
                                                                                    placeholder={t('contacts.firstName')}
                                                                                    className="contacts-record-card-input"
                                                                                    bordered={false}
                                                                                    onClick={(e) => e.stopPropagation()}
                                                                                />
                                                                                <Input
                                                                                    value={member.lastname}
                                                                                    onChange={(e) => handleGroupMemberFieldChange(record.key, idx, 'lastname', e.target.value)}
                                                                                    placeholder={t('contacts.lastName')}
                                                                                    className="contacts-record-card-input"
                                                                                    bordered={false}
                                                                                    onClick={(e) => e.stopPropagation()}
                                                                                />
                                                                                <div className="contacts-record-card-email-value">{member.email}</div>
                                                                                <Input
                                                                                    value={member.phone}
                                                                                    onChange={(e) => handleGroupMemberFieldChange(record.key, idx, 'phone', e.target.value)}
                                                                                    placeholder={t('contacts.phonePlaceholder')}
                                                                                    className="contacts-record-card-input"
                                                                                    bordered={false}
                                                                                    onClick={(e) => e.stopPropagation()}
                                                                                />
                                                                            </div>
                                                                            <div className="contacts-record-card-favorite-wrap">
                                                                                {memberContact ? (
                                                                                    <Button
                                                                                        type="text"
                                                                                        icon={isMemberFavorite ? <HeartFilled style={{ color: '#ff4d4f' }} /> : <HeartOutlined />}
                                                                                        className="contacts-record-card-favorite-btn"
                                                                                        onClick={(e) => handleToggleFavorite(memberContact, e)}
                                                                                    />
                                                                                ) : (
                                                                                    <span className="contacts-record-card-favorite-spacer" />
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="contacts-record-card-group-actions">
                                                        <Button
                                                            type="text"
                                                            icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
                                                            className="contacts-record-card-group-collapse-btn"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setGroupExpanded(prev => {
                                                                    const next = new Set(prev);
                                                                    if (next.has(record.key)) next.delete(record.key);
                                                                    else next.add(record.key);
                                                                    return next;
                                                                });
                                                            }}
                                                        />
                                                        <Button
                                                            type="text"
                                                            icon={<PlusOutlined />}
                                                            className="contacts-record-card-group-add-btn"
                                                            onClick={(e) => { e.stopPropagation(); setGroupAddModalKey(record.key); setGroupAddModalSearchText(''); setGroupAddModalDropdownOpen(false); }}
                                                        />
                                                        <Button
                                                            type="text"
                                                            icon={record.contact_type === 'favorite' ? <HeartFilled style={{ color: '#ff4d4f' }} /> : <HeartOutlined />}
                                                            className="contacts-record-card-group-favorite-btn"
                                                            onClick={(e) => handleToggleFavorite(record, e)}
                                                        />
                                                    </div>
                                                </div>
                                            );
                                        }
                                        return (
                                            <div
                                                key={record.key}
                                                className={`contacts-record-card contacts-record-card-editable contacts-record-card-with-favorite ${selectedContacts.includes(record.key) ? 'selected' : ''}`}
                                                onClick={(e) => { if (!e.target.closest('.contacts-record-card-checkbox') && !e.target.closest('input') && !e.target.closest('button')) handleContactSelect(record, e); }}
                                            >
                                                <Checkbox
                                                    checked={selectedContacts.includes(record.key)}
                                                    onChange={(e) => handleCheckboxChange(record.key, e.target.checked, e)}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="contacts-record-card-checkbox"
                                                />
                                                <Avatar
                                                    style={{ backgroundColor: getAvatarColor(record.firstname || record.email) }}
                                                    size={40}
                                                >
                                                    {getInitials(getContactEditValue(record, 'firstname'), getContactEditValue(record, 'lastname'))}
                                                </Avatar>
                                                <div className="contacts-record-card-fields contacts-record-card-fields--single-line">
                                                    <Input
                                                        value={getContactEditValue(record, 'firstname')}
                                                        onChange={(e) => handleContactFieldChange(record.key, 'firstname', e.target.value)}
                                                        placeholder={t('contacts.firstName')}
                                                        className="contacts-record-card-input"
                                                        bordered={false}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                    <Input
                                                        value={getContactEditValue(record, 'lastname')}
                                                        onChange={(e) => handleContactFieldChange(record.key, 'lastname', e.target.value)}
                                                        placeholder={t('contacts.lastName')}
                                                        className="contacts-record-card-input"
                                                        bordered={false}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                    <div className="contacts-record-card-email-value">{record.email}</div>
                                                    <Input
                                                        value={getContactEditValue(record, 'phone')}
                                                        onChange={(e) => handleContactFieldChange(record.key, 'phone', e.target.value)}
                                                        placeholder={t('contacts.phonePlaceholder')}
                                                        className="contacts-record-card-input"
                                                        bordered={false}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                </div>
                                                <div className="contacts-record-card-favorite-wrap">
                                                    <Button
                                                        type="text"
                                                        icon={record.contact_type === 'favorite' ? <HeartFilled style={{ color: '#ff4d4f' }} /> : <HeartOutlined />}
                                                        className="contacts-record-card-favorite-btn"
                                                        onClick={(e) => handleToggleFavorite(record, e)}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                        </div>
                                    )}
                            </div>
                        </Spin>
                    </>
                ) : (
                    <>
                        <div className="contacts-sidebar">
                            <div className="contacts-search-row">
                                <Checkbox
                                    checked={sortedData.length > 0 && sortedData.every(r => selectedContacts.includes(r.key))}
                                    indeterminate={sortedData.some(r => selectedContacts.includes(r.key)) && !sortedData.every(r => selectedContacts.includes(r.key))}
                                    onChange={(e) => {
                                        if (e.target.checked) setSelectedContacts(sortedData.map(r => r.key));
                                        else setSelectedContacts([]);
                                    }}
                                    className="contacts-select-all-checkbox"
                                />
                                <Input
                                    placeholder={t('contacts.searchContacts')}
                                    prefix={<SearchOutlined />}
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                    className="contacts-search-input"
                                />
                                {sortDropdown}
                                <Tooltip title={t('contacts.addNew')} placement="bottom">
                                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAddNew} className="contacts-add-btn-inline" aria-label={t('contacts.addNew')} />
                                </Tooltip>
                            </div>
                            <Spin spinning={loading}>
                                <div className="contacts-list">
                                    {sortedData.length === 0 ? (
                                        <div className="contacts-empty">
                                            <UserOutlined style={{ fontSize: '48px', color: '#ccc' }} />
                                            <p>{t('contacts.noContactsFound')}</p>
                                        </div>
                                    ) : (
                                        <>
                                            {sortedData.map((record) => {
                                                const isGroupRecord = record._type === 'group';
                                                return (
                                                    <div key={record.key} className={`contacts-card ${selectedContact?.key === record.key ? 'selected' : ''}`} onClick={(e) => handleContactSelect(record, e)}>
                                                        <Checkbox checked={selectedContacts.includes(record.key)} onChange={(e) => handleCheckboxChange(record.key, e.target.checked, e)} onClick={(e) => e.stopPropagation()} className="contacts-card-checkbox" style={{ marginRight: '12px' }} />
                                                        <Avatar style={{ backgroundColor: getAvatarColor(record.firstname || record.email), flexShrink: 0, marginRight: '12px' }} size={48} icon={isGroupRecord ? <FontAwesomeIcon icon={faUsers} /> : null}>
                                                            {isGroupRecord ? null : getInitials(record.firstname, record.lastname)}
                                                        </Avatar>
                                                        <div className="contacts-card-info">
                                                            <div className="contacts-card-name">{isGroupRecord ? (record.firstname || t('contacts.noName')) : (record.firstname || record.lastname ? `${record.firstname || ''} ${record.lastname || ''}`.trim() : t('contacts.noName'))}</div>
                                                            <div className="contacts-card-email">{isGroupRecord ? `${(record.members || record.email?.split(';') || []).length} ${t('contacts.members').toLowerCase()}` : record.email}</div>
                                                        </div>
                                                        <div className="contacts-card-badge" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                                            {isGroupRecord && <FontAwesomeIcon icon={faUsers} style={{ color: '#52c41a', fontSize: '18px' }} />}
                                                            <Button
                                                                type="text"
                                                                icon={record.contact_type === 'favorite' ? <HeartFilled style={{ color: '#ff4d4f', fontSize: '18px' }} /> : <HeartOutlined style={{ fontSize: '18px' }} />}
                                                                className="contacts-card-favorite-btn"
                                                                onClick={(e) => handleToggleFavorite(record, e)}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            <div className="contacts-list-spacer" />
                                        </>
                                    )}
                                </div>
                            </Spin>
                        </div>
                        <div className="contacts-detail-panel">
                    {!selectedContact ? (
                        <div className="contacts-detail-empty">
                            <UserOutlined style={{ fontSize: '64px', color: '#e0e0e0' }} />
                            <h3>{t('contacts.selectContact')}</h3>
                            <p>{t('contacts.selectContactDescription')}</p>
                        </div>
                    ) : (
                        <div className="contacts-detail-content">
                            {(() => {
                                const isGroupSelected = selectedContact._type === 'group';
                                return (
                                    <>
                                        <div className="contacts-detail-header">
                                            <div className="contacts-detail-header-left">
                                                <Avatar 
                                                    style={{ 
                                                        backgroundColor: getAvatarColor(selectedContact.firstname || selectedContact.email),
                                                    }}
                                                    size={80}
                                                    icon={isGroupSelected ? <FontAwesomeIcon icon={faUsers} /> : null}
                                                >
                                                    {isGroupSelected ? null : getInitials(selectedContact.firstname, selectedContact.lastname)}
                                                </Avatar>
                                                <div className="contacts-detail-header-info">
                                                    <h2 className="contacts-detail-header-title">
                                                        {isGroupSelected ? (
                                                            editingGroupName && activeTab !== 'deleted' ? (
                                                                <Input
                                                                    value={editFormData.firstname}
                                                                    onChange={(e) => handleFieldChange('firstname', e.target.value)}
                                                                    onBlur={() => setEditingGroupName(false)}
                                                                    onPressEnter={() => setEditingGroupName(false)}
                                                                    className="contacts-detail-input contacts-group-name-input"
                                                                    bordered={false}
                                                                    autoFocus
                                                                />
                                                            ) : (
                                                                <>
                                                                    {selectedContact.firstname || t('contacts.noName')}
                                                                    {activeTab !== 'deleted' && (
                                                                        <EditOutlined 
                                                                            className="contacts-edit-group-name-icon"
                                                                            onClick={() => setEditingGroupName(true)}
                                                                        />
                                                                    )}
                                                                </>
                                                            )
                                                        ) : (
                                                            selectedContact.firstname || selectedContact.lastname 
                                                                ? `${selectedContact.firstname || ''} ${selectedContact.lastname || ''}`.trim() 
                                                                : t('contacts.noName')
                                                        )}
                                                    </h2>
                                                    <p>
                                                        {isGroupSelected
                                                            ? `${(selectedContact.members || selectedContact.email?.split(';') || []).length} ${t('contacts.members').toLowerCase()}`
                                                            : selectedContact.email}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="contacts-detail-header-right">
                                                {selectedContact.created_at}
                                            </div>
                                        </div>
                                    </>
                                );
                            })()}

                            {/* Contact Details */}
                            {(() => {
                                const isGroupSelected = selectedContact._type === 'group';
                                return (
                                    <div className="contacts-detail-section">
                                        {isGroupSelected ? (
                                    // Group display: member cards (same layout as contacts) + empty add member card
                                    <div className="contacts-detail-field-group contacts-members-list-stacked">
                                        {groupMembers.map((member) => (
                                            <div key={member.email} className="contacts-contact-info-banner contacts-member-card contacts-member-card-with-remove">
                                                {activeTab !== 'deleted' && (
                                                    <Button
                                                        type="text"
                                                        danger
                                                        size="small"
                                                        icon={<DeleteOutlined />}
                                                        onClick={(e) => handleRemoveMember(member.email, e)}
                                                        className="contacts-member-remove-icon"
                                                        aria-label={t('contacts.remove')}
                                                    />
                                                )}
                                                <div className="contacts-email-banner">
                                                    <div className="contacts-email-banner-value" title={member.email}>{member.email}</div>
                                                </div>
                                                <div className="contacts-contact-info-fields">
                                                    <div className="contacts-detail-item contacts-detail-item-stacked">
                                                        {activeTab !== 'deleted' ? (
                                                            <Input
                                                                value={member.firstname}
                                                                onChange={(e) => handleMemberFieldChange(member.email, 'firstname', e.target.value)}
                                                                placeholder={t('contacts.firstName')}
                                                                className="contacts-detail-input"
                                                                bordered={false}
                                                            />
                                                        ) : (
                                                            <div className="contacts-detail-value">{member.firstname || '-'}</div>
                                                        )}
                                                    </div>
                                                    <div className="contacts-detail-item contacts-detail-item-stacked">
                                                        {activeTab !== 'deleted' ? (
                                                            <Input
                                                                value={member.lastname}
                                                                onChange={(e) => handleMemberFieldChange(member.email, 'lastname', e.target.value)}
                                                                placeholder={t('contacts.lastName')}
                                                                className="contacts-detail-input"
                                                                bordered={false}
                                                            />
                                                        ) : (
                                                            <div className="contacts-detail-value">{member.lastname || '-'}</div>
                                                        )}
                                                    </div>
                                                    <div className="contacts-detail-item contacts-detail-item-stacked">
                                                        {activeTab !== 'deleted' ? (
                                                            <Input
                                                                value={member.phone}
                                                                onChange={(e) => handleMemberFieldChange(member.email, 'phone', e.target.value)}
                                                                placeholder={t('contacts.phonePlaceholder')}
                                                                className="contacts-detail-input"
                                                                bordered={false}
                                                            />
                                                        ) : (
                                                            <div className={`contacts-detail-value ${!member.phone ? 'contacts-detail-value-empty' : ''}`}>
                                                                {member.phone || t('contacts.noPhoneProvided')}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                        {activeTab !== 'deleted' && (
                                            <div className="contacts-contact-info-banner contacts-member-card contacts-member-card-add">
                                                <div className="contacts-contact-info-fields contacts-add-member-fields">
                                                    <Input
                                                        placeholder={t('contacts.emailPlaceholder')}
                                                        value={newMemberEmail}
                                                        onChange={(e) => setNewMemberEmail(e.target.value)}
                                                        onPressEnter={handleAddMember}
                                                        onFocus={(e) => e.target.removeAttribute('readonly')}
                                                        className="contacts-detail-input"
                                                        bordered={false}
                                                        autoComplete="new-password"
                                                        autoCorrect="off"
                                                        autoCapitalize="off"
                                                        spellCheck={false}
                                                        data-lpignore="true"
                                                        data-form-type="other"
                                                        data-1p-ignore
                                                        name="contacts-add-member-search"
                                                        readOnly
                                                    />
                                                </div>
                                                <Tooltip title={t('contacts.addMember')} placement="bottom">
                                                    <Button
                                                        type="primary"
                                                        icon={<PlusOutlined />}
                                                        onClick={handleAddMember}
                                                        className="contacts-add-member-btn-inline"
                                                        aria-label={t('contacts.addMember')}
                                                    />
                                                </Tooltip>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    // Regular contact display
                                    <>
                                        <div className="contacts-detail-field-group">
                                            <div className="contacts-contact-info-banner">
                                                <div className="contacts-email-banner">
                                                    <div className="contacts-email-banner-value" title={selectedContact.email}>{selectedContact.email}</div>
                                                </div>
                                                <div className="contacts-contact-info-fields">
                                                    <div className="contacts-detail-item contacts-detail-item-stacked">
                                                        {activeTab !== 'deleted' ? (
                                                            <Input 
                                                                value={editFormData.firstname}
                                                                onChange={(e) => handleFieldChange('firstname', e.target.value)}
                                                                placeholder={t('contacts.firstName')}
                                                                className="contacts-detail-input"
                                                                bordered={false}
                                                            />
                                                        ) : (
                                                            <div className="contacts-detail-value">{selectedContact.firstname || '-'}</div>
                                                        )}
                                                    </div>
                                                    <div className="contacts-detail-item contacts-detail-item-stacked">
                                                        {activeTab !== 'deleted' ? (
                                                            <Input 
                                                                value={editFormData.lastname}
                                                                onChange={(e) => handleFieldChange('lastname', e.target.value)}
                                                                placeholder={t('contacts.lastName')}
                                                                className="contacts-detail-input"
                                                                bordered={false}
                                                            />
                                                        ) : (
                                                            <div className="contacts-detail-value">{selectedContact.lastname || '-'}</div>
                                                        )}
                                                    </div>
                                                    <div className="contacts-detail-item contacts-detail-item-stacked">
                                                        {activeTab !== 'deleted' ? (
                                                            <Input 
                                                                value={editFormData.phone}
                                                                onChange={(e) => handleFieldChange('phone', e.target.value)}
                                                                placeholder={t('contacts.phonePlaceholder')}
                                                                className="contacts-detail-input"
                                                                bordered={false}
                                                            />
                                                        ) : (
                                                            <div className={`contacts-detail-value ${!selectedContact.phone ? 'contacts-detail-value-empty' : ''}`}>
                                                                {selectedContact.phone || t('contacts.noPhoneProvided')}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                        )}
                                    </div>
                                );
                            })()}

                        </div>
                    )}
                </div>
                    </>
                )}
            </div>
            </div>

            {groupAddModalKey && (() => {
                const record = contacts.find(c => c.key === groupAddModalKey);
                if (!record) return null;
                const modalMembers = getGroupMembers(record);
                const filteredForGroup = filterContactsForGroup(groupAddModalSearchText);
                const addAsNew = (() => {
                    const trimmed = groupAddModalSearchText.trim();
                    if (!trimmed || !isValidEmail(trimmed)) return null;
                    if (modalMembers.some(m => m.email?.toLowerCase() === trimmed.toLowerCase())) return null;
                    if (filteredForGroup.some(c => c.email?.toLowerCase() === trimmed.toLowerCase())) return null;
                    return trimmed;
                })();
                const isInGroup = (email) => modalMembers.some(m => m.email?.toLowerCase() === email?.toLowerCase());
                return (
                    <Modal
                        title={getGroupName(record)}
                        open={!!groupAddModalKey}
                        onCancel={() => { setGroupAddModalKey(null); setGroupAddModalSearchText(''); setGroupAddModalDropdownOpen(false); }}
                        footer={null}
                        className="contacts-group-add-members-modal"
                    >
                        <div className="contacts-group-add-members-content">
                            <div className="contacts-group-add-members-input-wrap">
                                <Input
                                    placeholder={t('contacts.addFromContactsOrEmail')}
                                    value={groupAddModalSearchText}
                                    onChange={(e) => { setGroupAddModalSearchText(e.target.value); setGroupAddModalDropdownOpen(true); }}
                                    onFocus={(e) => { setGroupAddModalDropdownOpen(true); e.target.removeAttribute('readonly'); }}
                                    onBlur={() => setTimeout(() => setGroupAddModalDropdownOpen(false), 150)}
                                    autoComplete="new-password"
                                    autoCorrect="off"
                                    autoCapitalize="off"
                                    spellCheck={false}
                                    data-lpignore="true"
                                    data-form-type="other"
                                    data-1p-ignore
                                    name="contacts-add-member-search"
                                    readOnly
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && groupAddModalSearchText.trim()) {
                                            const trimmed = groupAddModalSearchText.trim();
                                            if (isValidEmail(trimmed)) {
                                                e.preventDefault();
                                                handleAddGroupMember(groupAddModalKey, { email: trimmed, firstname: '', lastname: '', phone: '' });
                                                setGroupAddModalSearchText('');
                                            } else if (filteredForGroup[0]) {
                                                e.preventDefault();
                                                const c = filteredForGroup[0];
                                                if (isInGroup(c.email)) handleRemoveGroupMemberByEmail(groupAddModalKey, c.email);
                                                else handleAddGroupMember(groupAddModalKey, { email: c.email, firstname: c.firstname || '', lastname: c.lastname || '', phone: c.phone || '' });
                                                setGroupAddModalSearchText('');
                                            }
                                        }
                                    }}
                                />
                                {(filteredForGroup.length > 0 || addAsNew) && groupAddModalDropdownOpen && (
                                    <div className="contacts-group-add-members-dropdown">
                                        {filteredForGroup.map(contact => {
                                            const inGroup = isInGroup(contact.email);
                                            return (
                                                <div
                                                    key={contact.email}
                                                    className={`contact-item ${inGroup ? 'contact-item-in-group' : ''}`}
                                                    onMouseDown={(e) => {
                                                        e.preventDefault();
                                                        if (inGroup) handleRemoveGroupMemberByEmail(groupAddModalKey, contact.email);
                                                        else handleAddGroupMember(groupAddModalKey, { email: contact.email, firstname: contact.firstname || '', lastname: contact.lastname || '', phone: contact.phone || '' });
                                                        setGroupAddModalSearchText('');
                                                    }}
                                                >
                                                    <span className="contact-item-content">
                                                        {contact.firstname || contact.lastname ? `${(contact.firstname || '')} ${(contact.lastname || '')}`.trim() + ` (${contact.email})` : contact.email}
                                                    </span>
                                                    <span className="contact-item-action">{inGroup ? <MinusOutlined /> : <PlusOutlined />}</span>
                                                </div>
                                            );
                                        })}
                                        {addAsNew && (
                                            <div
                                                className="contact-item contact-item-add-new"
                                                onMouseDown={(e) => { e.preventDefault(); handleAddGroupMember(groupAddModalKey, { email: addAsNew, firstname: '', lastname: '', phone: '' }); setGroupAddModalSearchText(''); }}
                                            >
                                                <span className="contact-item-content">{t('contacts.addAsNewMember', { email: addAsNew })}</span>
                                                <PlusOutlined className="contact-item-action" />
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            {modalMembers.length > 0 && (
                                <div className="contacts-group-add-members-list">
                                    {modalMembers.map((member, idx) => (
                                        <div key={idx} className="contacts-group-add-members-member-row">
                                            <span className="contacts-group-add-members-email-value">{member.email}</span>
                                            <span className="contact-add-member-email-block-remove" onClick={() => handleRemoveGroupMember(groupAddModalKey, idx)} title={t('contacts.remove')}>
                                                <DeleteOutlined />
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </Modal>
                );
            })()}

            <ContactGroupModal
                visible={isModalVisible}
                onClose={() => { setIsModalVisible(false); setEditingContact(null); }}
                onAdded={handleAdded}
                type={modalType}
                allowTypeToggle
                availableContacts={contacts}
                editingContact={editingContact}
            />
        </div>
    );
};

export default Contacts;
